import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  QrCode,
  Download,
  Plus,
  Pencil,
  Trash2,
  User,
  Send,
  BadgeCheck,
  Copy,
  Check,
  Loader2,
  BarChart3,
  KeyRound,
  ScanLine,
  TrendingUp,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import type { ExhibitorCompanyAccessDto, GrantExhibitorPortalResponse } from "@mvk/shared";
import { companiesService } from "../../services/companiesService";
import { catalogService } from "../../services/catalogService";
import { leadsService } from "../../services/leadsService";
import { adminAnalyticsApi } from "../../services/adminAnalyticsApi";
import { AdminModal } from "../components/AdminModal";
import { RepEditorModal, type RepFormValues } from "../components/RepEditorModal";
import { ProductEditorModal, type ProductFormValues } from "../components/ProductEditorModal";
import { CodeCard } from "../../components/CodeCard";
import { CompanyAvatar } from "../../components/CompanyAvatar";
import type { Company, Product, Representative } from "../../types";
import { downloadQrPng } from "../../utils/codeImages";

type CompanyForm = {
  name: string;
  category: string;
  shortAbout: string;
  phone: string;
  email: string;
  website: string;
};

function companyToForm(company: Company): CompanyForm {
  return {
    name: company.name,
    category: company.category,
    shortAbout: company.shortAbout,
    phone: company.contact.phone ?? "",
    email: company.contact.email ?? "",
    website: company.website ?? "",
  };
}

export function AdminExhibitorDetailPage() {
  const { id, companyId } = useParams<{ id: string; companyId: string }>();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddRep, setShowAddRep] = useState(false);
  const [activeRep, setActiveRep] = useState<{ rep: Representative; mode: "view" | "edit" } | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [activeProduct, setActiveProduct] = useState<{ product: Product; mode: "view" | "edit" } | null>(null);
  const [productsQrBusy, setProductsQrBusy] = useState(false);
  const [company, setCompany] = useState<Company | undefined>();
  const [companyForm, setCompanyForm] = useState<CompanyForm | null>(null);
  const [reps, setReps] = useState<Representative[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState<ExhibitorCompanyAccessDto | null>(null);
  const [accessBusy, setAccessBusy] = useState(false);
  const [showPortalGrant, setShowPortalGrant] = useState(false);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalName, setPortalName] = useState("");
  const [portalResult, setPortalResult] = useState<GrantExhibitorPortalResponse | null>(null);
  const [copiedPwd, setCopiedPwd] = useState(false);
  const [inboundCount, setInboundCount] = useState<number | null>(null);
  const [outboundCount, setOutboundCount] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (!id || !companyId) return;
    setLoading(true);
    try {
      const [comp, repList, productList, accessRes, inbound, outbound] = await Promise.all([
        companiesService.getById(companyId, id, "admin"),
        catalogService.listRepresentatives(id, companyId, "admin"),
        catalogService.listProducts(id, companyId, "admin"),
        adminAnalyticsApi.getCompanyAccess(id, companyId).catch(() => null),
        leadsService
          .listByCompany(id, companyId, "admin", "inbound")
          .then((r) => r.leads.length)
          .catch(() => null),
        leadsService
          .listByCompany(id, companyId, "admin", "outbound")
          .then((r) => r.leads.length)
          .catch(() => null),
      ]);
      setCompany(comp);
      setReps(repList);
      setProducts(productList);
      setAccess(accessRes?.access ?? null);
      setInboundCount(inbound);
      setOutboundCount(outbound);
      if (comp) {
        setCompanyForm(companyToForm(comp));
        setPortalEmail((prev) => prev || comp.contact.email || "");
        setPortalName((prev) => prev || comp.name);
      }
    } catch {
      setCompany(undefined);
      setReps([]);
      setProducts([]);
      setCompanyForm(null);
      setAccess(null);
      setInboundCount(null);
      setOutboundCount(null);
    } finally {
      setLoading(false);
    }
  }, [id, companyId]);

  const handleGrantPortal = async () => {
    if (!id || !companyId) return;
    const email = portalEmail.trim();
    if (!email.includes("@")) {
      toast.error("Укажите email для входа");
      return;
    }
    setAccessBusy(true);
    try {
      const res = await adminAnalyticsApi.grantPortal(id, companyId, {
        email,
        fullName: portalName.trim() || undefined,
        enableAnalytics: true,
      });
      setPortalResult(res.portal);
      setAccess((prev) =>
        prev
          ? {
              ...prev,
              analyticsEnabled: true,
              canViewAnalytics: true,
              portalUsers: [
                ...prev.portalUsers.filter((u) => u.userId !== res.portal.userId),
                {
                  userId: res.portal.userId,
                  email: res.portal.email,
                  fullName: res.portal.fullName,
                  hasPassword: true,
                },
              ],
            }
          : prev,
      );
      toast.success("Доступ в админку выдан");
    } catch {
      toast.error("Не удалось выдать доступ");
    } finally {
      setAccessBusy(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSaveCompany = async () => {
    if (!id || !companyId || !companyForm) return;
    setSaving(true);
    try {
      const updated = await companiesService.updateProfile(id, companyId, {
        name: companyForm.name.trim(),
        category: companyForm.category.trim(),
        shortAbout: companyForm.shortAbout.trim(),
        phone: companyForm.phone.trim() || undefined,
        email: companyForm.email.trim() || undefined,
        website: companyForm.website.trim() || undefined,
      });
      if (updated) {
        setCompany(updated);
        setCompanyForm(companyToForm(updated));
      }
      setEditMode(false);
      toast.success("Изменения сохранены");
    } catch {
      toast.error("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRep = async (data: RepFormValues) => {
    if (!id || !companyId) return;
    const payload = {
      fullName: data.fullName.trim(),
      position: data.position.trim(),
      phone: data.phone.trim() || undefined,
      email: data.email.trim() || undefined,
      telegram: data.telegram.trim() || undefined,
      bio: data.bio.trim() || undefined,
    };
    if (activeRep) {
      await catalogService.updateRepresentative(id, companyId, activeRep.rep.id, payload);
    } else {
      await catalogService.createRepresentative(id, companyId, payload);
    }
    await loadData();
  };

  const handleSaveProduct = async (data: ProductFormValues) => {
    if (!id || !companyId) return;
    const payload = {
      name: data.name,
      category: data.category || undefined,
      description: data.description || undefined,
      price: data.price || undefined,
      ...(data.clearImage
        ? { images: [] as string[] }
        : data.images !== undefined
          ? { images: data.images }
          : {}),
    };
    if (activeProduct) {
      await catalogService.updateProduct(id, companyId, activeProduct.product.id, payload);
    } else {
      await catalogService.createProduct(id, companyId, payload);
    }
    await loadData();
  };

  const handleDeleteProduct = async (product: Product) => {
    if (!id || !companyId) return;
    if (!window.confirm(`Удалить товар «${product.name}»?`)) return;
    try {
      await catalogService.deleteProduct(id, companyId, product.id);
      toast.success("Товар удалён");
      await loadData();
    } catch {
      toast.error("Не удалось удалить товар");
    }
  };

  const handleDownloadAllProductQrs = async () => {
    const withQr = products.filter((p) => p.qrCode);
    if (!withQr.length) {
      toast.error("Нет товаров с QR");
      return;
    }
    setProductsQrBusy(true);
    try {
      for (const product of withQr) {
        await downloadQrPng(product.qrCode!, `${product.name}-product`);
      }
      toast.success(`Сохранено QR: ${withQr.length}`);
    } catch {
      toast.error("Не удалось скачать QR товаров");
    } finally {
      setProductsQrBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!company || !companyForm) return <div className="p-6 text-gray-500">Компания не найдена</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      {(showAddRep || activeRep) && (
        <RepEditorModal
          mode={showAddRep ? "create" : activeRep?.mode === "edit" ? "edit" : "view"}
          initial={activeRep?.rep}
          onClose={() => {
            setShowAddRep(false);
            setActiveRep(null);
          }}
          onSave={handleSaveRep}
        />
      )}

      {(showAddProduct || activeProduct) && (
        <ProductEditorModal
          mode={showAddProduct ? "create" : activeProduct?.mode === "edit" ? "edit" : "view"}
          initial={activeProduct?.product}
          onClose={() => {
            setShowAddProduct(false);
            setActiveProduct(null);
          }}
          onSave={handleSaveProduct}
        />
      )}

      {showPortalGrant && (
        <AdminModal
          title="Доступ в админку экспонента"
          onClose={() => {
            setShowPortalGrant(false);
            setPortalResult(null);
            setCopiedPwd(false);
          }}
          footer={
            portalResult ? (
              <button
                type="button"
                onClick={() => {
                  setShowPortalGrant(false);
                  setPortalResult(null);
                }}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-[13px]"
              >
                Готово
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowPortalGrant(false)}
                  className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  disabled={accessBusy}
                  onClick={() => void handleGrantPortal()}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] disabled:opacity-60"
                >
                  {accessBusy ? "Выдача…" : "Выдать доступ"}
                </button>
              </>
            )
          }
        >
          {portalResult ? (
            <div className="space-y-3">
              <p className="text-[13px] text-gray-700">
                Аккаунт создан. Передайте экспоненту данные для входа в админку.
              </p>
              <div className="p-3 rounded-xl bg-gray-50 text-[13px] space-y-1">
                <div>
                  <span className="text-gray-400">Email: </span>
                  <span className="text-gray-900">{portalResult.email}</span>
                </div>
                {portalResult.temporaryPassword && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Пароль: </span>
                    <code className="text-gray-900 font-mono">{portalResult.temporaryPassword}</code>
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-white text-gray-400"
                      onClick={async () => {
                        await navigator.clipboard.writeText(portalResult.temporaryPassword!);
                        setCopiedPwd(true);
                        toast.success("Пароль скопирован");
                      }}
                    >
                      {copiedPwd ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[12px] text-gray-500">{portalResult.loginHint}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[12px] text-gray-500">
                Создаст логин в админку и откроет аналитику стенда (просмотры, сканы, кто смотрел).
              </p>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">Email для входа*</label>
                <input
                  value={portalEmail}
                  onChange={(e) => setPortalEmail(e.target.value)}
                  placeholder="exhibitor@company.ru"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">Имя в кабинете</label>
                <input
                  value={portalName}
                  onChange={(e) => setPortalName(e.target.value)}
                  placeholder="Менеджер стенда"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
                />
              </div>
            </div>
          )}
        </AdminModal>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/events/${id}/exhibitors`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={18} />
        </button>
        <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="md" className="rounded-xl" />
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">{company.name}</h1>
          <p className="text-[12px] text-gray-400">{company.category} · Стенд {company.booth}</p>
        </div>
        <button
          onClick={() => {
            if (editMode) {
              void handleSaveCompany();
            } else {
              setEditMode(true);
            }
          }}
          disabled={saving}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
            editMode ? "bg-indigo-600 text-white" : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-200 hover:text-indigo-600"
          } disabled:opacity-60`}
        >
          <Pencil size={14} /> {editMode ? (saving ? "Сохранение…" : "Сохранить") : "Редактировать"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — main info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Company info */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-900 mb-3">Основная информация</h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "name" as const, label: "Название", col: "col-span-2" },
                { key: "category" as const, label: "Категория" },
              ].map((f) => (
                <div key={f.key} className={f.col}>
                  <label className="text-[11px] text-gray-400 mb-1 block">{f.label}</label>
                  {editMode ? (
                    <input
                      value={companyForm[f.key]}
                      onChange={(e) => setCompanyForm({ ...companyForm, [f.key]: e.target.value })}
                      className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    />
                  ) : (
                    <div className="text-[13px] text-gray-800">{companyForm[f.key]}</div>
                  )}
                </div>
              ))}
              <div>
                <label className="text-[11px] text-gray-400 mb-1 block">Стенд</label>
                <div className="text-[13px] text-gray-800">{company.booth}</div>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] text-gray-400 mb-1 block">Описание</label>
                {editMode ? (
                  <textarea
                    value={companyForm.shortAbout}
                    onChange={(e) => setCompanyForm({ ...companyForm, shortAbout: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
                  />
                ) : (
                  <div className="text-[13px] text-gray-700 leading-relaxed">{companyForm.shortAbout}</div>
                )}
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-900 mb-3">Общие контакты стенда</h2>
            <p className="text-[11px] text-gray-400 mb-3">
              Это контакты, которые посетитель увидит при сканировании QR-кода стенда.
            </p>
            <div className="space-y-2">
              {[
                { key: "phone" as const, label: "Телефон", icon: <Phone size={13} /> },
                { key: "email" as const, label: "Email", icon: <Mail size={13} /> },
                { key: "website" as const, label: "Сайт", icon: <Globe size={13} /> },
              ].map((f) => (
                <div key={f.key} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 flex-shrink-0">
                    {f.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-gray-400">{f.label}</div>
                    {editMode ? (
                      <input
                        value={companyForm[f.key]}
                        onChange={(e) => setCompanyForm({ ...companyForm, [f.key]: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 rounded-md border border-gray-200 text-[12px] outline-none focus:border-indigo-400"
                      />
                    ) : (
                      <div className="text-[13px] text-gray-800">{companyForm[f.key] || "—"}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Representatives */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-[13px] text-gray-900">Представители</h2>
                <p className="text-[11px] text-gray-400">Личные бейджи · персональные QR-коды</p>
              </div>
              <button
                onClick={() => {
                  setActiveRep(null);
                  setShowAddRep(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[12px] transition-colors"
              >
                <Plus size={13} /> Добавить
              </button>
            </div>
            {reps.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-gray-400 border-2 border-dashed border-gray-100 rounded-xl">
                Представители не добавлены
              </div>
            ) : (
              <div className="space-y-2">
                {reps.map((rep) => (
                  <div key={rep.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-indigo-50/30 transition-colors">
                    {rep.photo ? (
                      <img src={rep.photo} alt={rep.fullName}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <User size={18} className="text-indigo-500" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setActiveRep({ rep, mode: "view" })}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="text-[13px] text-gray-900">{rep.fullName}</div>
                      <div className="text-[11px] text-gray-500">{rep.position || "Должность не указана"}</div>
                      <div className="mt-1 space-y-0.5 text-[11px] text-gray-500">
                        <div className="flex items-center gap-1">
                          <Phone size={9} className="text-gray-400" />
                          {rep.phone || <span className="text-gray-400">телефон не указан</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Mail size={9} className="text-gray-400" />
                          {rep.email || <span className="text-gray-400">email не указан</span>}
                        </div>
                        {rep.telegram ? (
                          <div className="flex items-center gap-1 text-sky-600">
                            <Send size={9} /> {rep.telegram}
                          </div>
                        ) : null}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <div className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <BadgeCheck size={10} /> Бейдж
                      </div>
                      <button
                        type="button"
                        title="QR / баркод"
                        aria-label="QR / баркод"
                        onClick={() => setActiveRep({ rep, mode: "view" })}
                        className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-indigo-500 transition-colors"
                      >
                        <QrCode size={14} />
                      </button>
                      <button
                        type="button"
                        title="Смотреть и править данные"
                        aria-label="Редактировать"
                        onClick={() => setActiveRep({ rep, mode: "edit" })}
                        className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Products section */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-[13px] text-gray-900">Товары на выставке</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">Экспонируемые продукты и образцы · до 5 позиций</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setActiveProduct(null);
                  setShowAddProduct(true);
                }}
                disabled={products.length >= 5}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[12px] transition-colors disabled:opacity-50"
              >
                <Plus size={13} /> Добавить
              </button>
            </div>
            {products.length === 0 ? (
              <div className="py-8 text-center border-2 border-dashed border-gray-100 rounded-xl">
                <div className="text-[13px] text-gray-400">Товары не добавлены</div>
                <div className="text-[12px] text-gray-300 mt-1">Добавьте продукты, которые экспонент покажет на выставке</div>
              </div>
            ) : (
              <div className="space-y-2">
                {products.map((product) => (
                  <div key={product.id} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 hover:bg-indigo-50/30 transition-colors">
                    <button
                      type="button"
                      onClick={() => setActiveProduct({ product, mode: "view" })}
                      className="flex items-start gap-3 flex-1 min-w-0 text-left cursor-pointer"
                    >
                    {product.images[0] ? (
                      <img src={product.images[0]} alt={product.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <Building2 size={18} className="text-indigo-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-gray-900">{product.name}</div>
                      <div className="text-[11px] text-gray-400">{product.category}</div>
                      <p className="text-[12px] text-gray-500 mt-0.5 line-clamp-2 leading-snug">{product.description}</p>
                      {product.price && (
                        <div className="text-[11px] text-indigo-600 mt-1">{product.price}</div>
                      )}
                      {product.qrCode ? (
                        <div className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
                          <QrCode size={10} /> QR готов
                        </div>
                      ) : null}
                    </div>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        title="QR товара"
                        aria-label="QR товара"
                        onClick={() => setActiveProduct({ product, mode: "view" })}
                        className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-indigo-500 transition-colors cursor-pointer"
                      >
                        <QrCode size={14} />
                      </button>
                      <button
                        type="button"
                        title="Редактировать"
                        aria-label="Редактировать"
                        onClick={() => setActiveProduct({ product, mode: "edit" })}
                        className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        title="Удалить"
                        aria-label="Удалить"
                        onClick={() => void handleDeleteProduct(product)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right — scans + access + QR codes + booth */}
        <div className="space-y-4">
          {/* Same CRM views as exhibitor cabinet */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-indigo-500" />
              <h2 className="text-[13px] text-gray-900">Сканы и лиды</h2>
            </div>
            <p className="text-[11px] text-gray-400 mb-3 leading-snug">
              Тот же список, что в кабинете экспонента: кто сканировал стенд и кого стенд
              отсканировал.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => navigate(`/events/${id}/exhibitors/${companyId}/leads`)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-left transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={15} className="text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-gray-900">Вас сканировали</div>
                  <div className="text-[11px] text-gray-500">
                    {inboundCount === null
                      ? "Посетители → стенд / бейджи"
                      : inboundCount === 0
                        ? "Пока нет сканов"
                        : `${inboundCount} ${inboundCount === 1 ? "лид" : inboundCount < 5 ? "лида" : "лидов"}`}
                  </div>
                </div>
                <ChevronRight size={16} className="text-indigo-400 flex-shrink-0" />
              </button>
              <button
                type="button"
                onClick={() => navigate(`/events/${id}/exhibitors/${companyId}/you-scanned`)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-left transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <ScanLine size={15} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-gray-900">Вы сканировали</div>
                  <div className="text-[11px] text-gray-500">
                    {outboundCount === null
                      ? "Стенд → бейджи посетителей"
                      : outboundCount === 0
                        ? "Пока нет сканов"
                        : `${outboundCount} ${outboundCount === 1 ? "лид" : outboundCount < 5 ? "лида" : "лидов"}`}
                  </div>
                </div>
                <ChevronRight size={16} className="text-emerald-400 flex-shrink-0" />
              </button>
            </div>
          </div>

          {/* Analytics + portal access */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={16} className="text-indigo-500" />
              <h2 className="text-[13px] text-gray-900">Доступ экспонента</h2>
            </div>
            <p className="text-[11px] text-gray-400 mb-3 leading-snug">
              Аналитика стенда доступна каждому экспоненту по умолчанию (своя компания и кто
              сканировал). Выдайте логин — экспонент войдёт в кабинет.
            </p>

            {access ? (
              <div className="space-y-3">
                {access.portalUsers.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[11px] text-gray-400">Учётки в админке</div>
                    {access.portalUsers.map((u) => (
                      <div
                        key={u.userId}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 text-[12px]"
                      >
                        <KeyRound size={12} className="text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-gray-800 truncate">{u.fullName}</div>
                          <div className="text-[10px] text-gray-400 truncate">{u.email ?? "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setPortalResult(null);
                    setCopiedPwd(false);
                    setShowPortalGrant(true);
                  }}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[12px] transition-colors"
                >
                  <KeyRound size={13} /> Выдать доступ в админку
                </button>
              </div>
            ) : (
              <p className="text-[12px] text-gray-400">Не удалось загрузить настройки доступа</p>
            )}
          </div>

          {/* Booth info */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-900 mb-3">Стенд</h2>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                <MapPin size={14} className="text-indigo-400" />
                <span className="text-[13px] text-gray-800">{company.booth}</span>
              </div>
              <div className="text-[12px] text-gray-500">Павильон {company.hall}</div>
              <button
                onClick={() => navigate(`/events/${id}/map`)}
                className="w-full py-2 rounded-lg border border-dashed border-gray-200 text-[12px] text-gray-500 hover:border-indigo-200 hover:text-indigo-500 transition-colors mt-1"
              >
                Показать на карте →
              </button>
            </div>
          </div>

          {/* Booth QR */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-900 mb-3">QR-код стенда</h2>
            <p className="text-[11px] text-gray-400 mb-3 leading-snug">
              Размещается на флажке стенда. Открывает общие контакты компании.
            </p>
            <CodeCard
              title="QR стенда"
              subtitle="Размещается на флажке стенда. Открывает общие контакты компании."
              qrValue={company.qrCodeValue}
              badge="Стенд"
              badgeColor="bg-indigo-100 text-indigo-700"
              downloadName={`${company.name}-stenad`}
            />
          </div>

          {/* Badge QRs */}
          {reps.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[13px] text-gray-900">QR бейджей</h2>
                <button className="flex items-center gap-1 text-[11px] text-indigo-500 hover:text-indigo-700">
                  <Download size={11} /> Все
                </button>
              </div>
              <div className="space-y-3">
                {reps.map((rep) => (
                  <CodeCard
                    key={rep.id}
                    title={rep.fullName}
                    subtitle={rep.position}
                    qrValue={rep.badgeQrCode}
                    barcodeValue={rep.badgeBarcode}
                    badge="Бейдж"
                    badgeColor="bg-emerald-100 text-emerald-700"
                    downloadName={rep.fullName}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Product QRs */}
          {products.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[13px] text-gray-900">QR товаров</h2>
                <button
                  type="button"
                  disabled={productsQrBusy}
                  onClick={() => void handleDownloadAllProductQrs()}
                  className="flex items-center gap-1 text-[11px] text-indigo-500 hover:text-indigo-700 disabled:opacity-60"
                >
                  <Download size={11} /> {productsQrBusy ? "…" : "Все"}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mb-3 leading-snug">
                Разместите на образцах на стенде — посетитель отсканирует и сохранит карточку товара.
              </p>
              <div className="space-y-3">
                {products.map((product) => (
                  <CodeCard
                    key={product.id}
                    title={product.name}
                    subtitle={product.category || "Товар на выставке"}
                    qrValue={product.qrCode}
                    badge="Товар"
                    badgeColor="bg-violet-100 text-violet-700"
                    downloadName={`${product.name}-product`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
